import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const initialFilters = {
    search: "",
    documentStatus: "",
    refundStatus: "",
    refundMethod: "",
    returnType: "",

    invoiceId: "",
    customerId: "",
    branchId: "",
    firmId: "",
    warehouseId: "",

    fromDate: "",
    toDate: "",

    sortBy: "returnDate",
    sortOrder: "desc",

    page: 1,
    limit: 20,
};

const initialPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
};

const initialSummary = {
    totalCount: 0,
    totalReturnAmount: 0,
    totalRefundAmount: 0,
    pendingRefundAmount: 0,

    draftCount: 0,
    confirmedCount: 0,
    cancelledCount: 0,

    fullReturnCount: 0,
    partialReturnCount: 0,
};

const initialReturnableSummary = {
    invoiceItemCount: 0,
    returnableItemCount: 0,
    totalSoldQuantity: 0,
    totalReturnedQuantity: 0,
    totalReturnableQuantity: 0,
    maximumRefundAmount: 0,
};

const initialState = {
    saleReturns: [],
    selectedSaleReturn: null,

    returnableInvoice: null,
    returnableItems: [],

    returnableSummary: {
        ...initialReturnableSummary,
    },

    summary: {
        ...initialSummary,
    },

    createData: null,

    dropdowns: {
        returnTypes: [],
        documentStatuses: [],
        refundStatuses: [],
        refundMethods: [],
        itemConditions: [],
        inventoryActions: [],
        returnReasons: [],
    },

    history: [],

    historyPagination: {
        ...initialPagination,
    },

    filters: {
        ...initialFilters,
    },

    pagination: {
        ...initialPagination,
    },

    listLoading: false,
    detailsLoading: false,
    summaryLoading: false,
    createDataLoading: false,
    dropdownsLoading: false,
    historyLoading: false,

    returnableInvoiceLoading: false,
    returnableItemsLoading: false,

    createLoading: false,
    confirmLoading: false,
    cancelLoading: false,
    deleteLoading: false,

    error: null,
    actionError: null,

    operationSuccess: false,
    operationMessage: "",

    lastCreatedSaleReturn: null,
    lastConfirmedSaleReturn: null,
    lastCreditNote: null,
    lastRefund: null,
    lastStockMovements: [],
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseData = (
    response,
    fallback = {}
) => {
    if (!response) {
        return fallback;
    }

    return (
        response.data ||
        response.result ||
        response
    );
};

const getErrorMessage = (
    response,
    fallbackMessage
) => {
    return (
        response?.message ||
        response?.error?.message ||
        response?.error ||
        fallbackMessage
    );
};

const rejectIfFailed = (
    response,
    rejectWithValue,
    fallbackMessage
) => {
    if (
        !response ||
        response.success === false ||
        response.status === "error"
    ) {
        return rejectWithValue(
            getErrorMessage(
                response,
                fallbackMessage
            )
        );
    }

    return null;
};

const extractSaleReturn = (
    payload
) => {
    return (
        payload?.saleReturn ||
        payload?.returnDocument ||
        payload
    );
};

const updateSaleReturnInList = (
    state,
    saleReturn
) => {
    if (!saleReturn?._id) {
        return;
    }

    const index =
        state.saleReturns.findIndex(
            (item) =>
                item._id ===
                saleReturn._id
        );

    if (index >= 0) {
        state.saleReturns[index] =
            saleReturn;
    }
};

const addSaleReturnToList = (
    state,
    saleReturn
) => {
    if (!saleReturn?._id) {
        return;
    }

    const exists =
        state.saleReturns.some(
            (item) =>
                item._id ===
                saleReturn._id
        );

    if (!exists) {
        state.saleReturns.unshift(
            saleReturn
        );
    }
};

/*
|--------------------------------------------------------------------------
| FETCH SALE RETURN LIST
|--------------------------------------------------------------------------
*/

export const fetchSaleReturns =
    createAsyncThunk(
        "saleReturn/fetchSaleReturns",
        async (
            params = {},
            {
                getState,
                rejectWithValue,
            }
        ) => {
            const filters =
                getState()
                    ?.saleReturn
                    ?.filters ||
                initialFilters;

            const response =
                await api.get(
                    config.getSaleReturns,
                    {
                        ...filters,
                        ...params,
                    }
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale returns."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnSummary =
    createAsyncThunk(
        "saleReturn/fetchSaleReturnSummary",
        async (
            params = {},
            {
                getState,
                rejectWithValue,
            }
        ) => {
            const filters =
                getState()
                    ?.saleReturn
                    ?.filters ||
                initialFilters;

            const response =
                await api.get(
                    config.getSaleReturnSummary,
                    {
                        ...filters,
                        ...params,

                        page:
                            undefined,

                        limit:
                            undefined,

                        sortBy:
                            undefined,

                        sortOrder:
                            undefined,
                    }
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale return summary."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH CREATE DATA
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnCreateData =
    createAsyncThunk(
        "saleReturn/fetchSaleReturnCreateData",
        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            const response =
                await api.get(
                    config.getSaleReturnCreateData,
                    params
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale return create data."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH DROPDOWNS
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnDropdowns =
    createAsyncThunk(
        "saleReturn/fetchSaleReturnDropdowns",
        async (
            _,
            {
                rejectWithValue,
            }
        ) => {
            const response =
                await api.get(
                    config.getSaleReturnDropdowns
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale return dropdowns."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH RETURNABLE INVOICE
|--------------------------------------------------------------------------
*/

export const fetchReturnableInvoice =
    createAsyncThunk(
        "saleReturn/fetchReturnableInvoice",
        async (
            invoiceId,
            {
                rejectWithValue,
            }
        ) => {
            if (!invoiceId) {
                return rejectWithValue(
                    "Sale invoice ID is required."
                );
            }

            const response =
                await api.get(
                    config.getReturnableInvoice(
                        invoiceId
                    )
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch returnable invoice."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH RETURNABLE ITEMS
|--------------------------------------------------------------------------
*/

export const fetchReturnableInvoiceItems =
    createAsyncThunk(
        "saleReturn/fetchReturnableInvoiceItems",
        async (
            invoiceId,
            {
                rejectWithValue,
            }
        ) => {
            if (!invoiceId) {
                return rejectWithValue(
                    "Sale invoice ID is required."
                );
            }

            const response =
                await api.get(
                    config.getReturnableInvoiceItems(
                        invoiceId
                    )
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch returnable invoice items."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH SALE RETURN BY ID
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnById =
    createAsyncThunk(
        "saleReturn/fetchSaleReturnById",
        async (
            saleReturnId,
            {
                rejectWithValue,
            }
        ) => {
            if (!saleReturnId) {
                return rejectWithValue(
                    "Sale return ID is required."
                );
            }

            const response =
                await api.get(
                    config.getSaleReturnById(
                        saleReturnId
                    )
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale return."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| CREATE SALE RETURN
|--------------------------------------------------------------------------
*/

export const createSaleReturn =
    createAsyncThunk(
        "saleReturn/createSaleReturn",
        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            if (
                !payload?.invoiceId &&
                !payload?.saleInvoiceId &&
                !payload?.sourceInvoiceId
            ) {
                return rejectWithValue(
                    "Sale invoice ID is required."
                );
            }

            if (
                !Array.isArray(
                    payload?.items
                ) ||
                payload.items.length === 0
            ) {
                return rejectWithValue(
                    "At least one return item is required."
                );
            }

            const response =
                await api.post(
                    config.createSaleReturn,
                    payload
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to create sale return."
                );

            if (failed) {
                return failed;
            }

            return {
                data:
                    getResponseData(
                        response,
                        {}
                    ),

                message:
                    response?.message ||
                    "Sale return created successfully.",
            };
        }
    );

/*
|--------------------------------------------------------------------------
| CONFIRM SALE RETURN
|--------------------------------------------------------------------------
*/

export const confirmSaleReturn =
    createAsyncThunk(
        "saleReturn/confirmSaleReturn",
        async (
            {
                saleReturnId,
                payload = {},
            },
            {
                rejectWithValue,
            }
        ) => {
            if (!saleReturnId) {
                return rejectWithValue(
                    "Sale return ID is required."
                );
            }

            const response =
                await api.patch(
                    config.confirmSaleReturn(
                        saleReturnId
                    ),
                    payload
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to confirm sale return."
                );

            if (failed) {
                return failed;
            }

            return {
                data:
                    getResponseData(
                        response,
                        {}
                    ),

                message:
                    response?.message ||
                    "Sale return confirmed successfully.",
            };
        }
    );

/*
|--------------------------------------------------------------------------
| CANCEL SALE RETURN
|--------------------------------------------------------------------------
*/

export const cancelSaleReturn =
    createAsyncThunk(
        "saleReturn/cancelSaleReturn",
        async (
            {
                saleReturnId,
                reason,
            },
            {
                rejectWithValue,
            }
        ) => {
            if (!saleReturnId) {
                return rejectWithValue(
                    "Sale return ID is required."
                );
            }

            if (!reason?.trim()) {
                return rejectWithValue(
                    "Cancellation reason is required."
                );
            }

            const response =
                await api.patch(
                    config.cancelSaleReturn(
                        saleReturnId
                    ),
                    {
                        reason:
                            reason.trim(),
                    }
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to cancel sale return."
                );

            if (failed) {
                return failed;
            }

            return {
                data:
                    getResponseData(
                        response,
                        {}
                    ),

                message:
                    response?.message ||
                    "Sale return cancelled successfully.",
            };
        }
    );

/*
|--------------------------------------------------------------------------
| DELETE SALE RETURN
|--------------------------------------------------------------------------
*/

export const deleteSaleReturn =
    createAsyncThunk(
        "saleReturn/deleteSaleReturn",
        async (
            saleReturnId,
            {
                rejectWithValue,
            }
        ) => {
            if (!saleReturnId) {
                return rejectWithValue(
                    "Sale return ID is required."
                );
            }

            const response =
                await api.delete(
                    config.deleteSaleReturn(
                        saleReturnId
                    )
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to delete sale return."
                );

            if (failed) {
                return failed;
            }

            return {
                saleReturnId,

                message:
                    response?.message ||
                    "Sale return deleted successfully.",
            };
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH HISTORY
|--------------------------------------------------------------------------
*/

export const fetchSaleReturnHistory =
    createAsyncThunk(
        "saleReturn/fetchSaleReturnHistory",
        async (
            {
                saleReturnId,
                params = {},
            },
            {
                rejectWithValue,
            }
        ) => {
            if (!saleReturnId) {
                return rejectWithValue(
                    "Sale return ID is required."
                );
            }

            const response =
                await api.get(
                    config.getSaleReturnHistory(
                        saleReturnId
                    ),
                    params
                );

            const failed =
                rejectIfFailed(
                    response,
                    rejectWithValue,
                    "Unable to fetch sale return history."
                );

            if (failed) {
                return failed;
            }

            return getResponseData(
                response,
                {}
            );
        }
    );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const saleReturnSlice =
    createSlice({
        name: "saleReturn",

        initialState,

        reducers: {
            setSaleReturnFilters: (
                state,
                action
            ) => {
                state.filters = {
                    ...state.filters,
                    ...(action.payload || {}),
                };
            },

            setSaleReturnFilter: (
                state,
                action
            ) => {
                const {
                    key,
                    value,
                } =
                    action.payload || {};

                if (!key) {
                    return;
                }

                state.filters[key] =
                    value;

                if (
                    key !== "page" &&
                    key !== "limit"
                ) {
                    state.filters.page = 1;
                }
            },

            setSaleReturnPage: (
                state,
                action
            ) => {
                state.filters.page =
                    Number(
                        action.payload
                    ) || 1;
            },

            setSaleReturnLimit: (
                state,
                action
            ) => {
                state.filters.limit =
                    Number(
                        action.payload
                    ) || 20;

                state.filters.page = 1;
            },

            resetSaleReturnFilters: (
                state
            ) => {
                state.filters = {
                    ...initialFilters,
                };
            },

            setSelectedSaleReturn: (
                state,
                action
            ) => {
                state.selectedSaleReturn =
                    action.payload || null;
            },

            clearSelectedSaleReturn: (
                state
            ) => {
                state.selectedSaleReturn =
                    null;
            },

            clearReturnableInvoice: (
                state
            ) => {
                state.returnableInvoice =
                    null;

                state.returnableItems =
                    [];

                state.returnableSummary = {
                    ...initialReturnableSummary,
                };

                state.returnableInvoiceLoading =
                    false;

                state.returnableItemsLoading =
                    false;
            },

            setReturnableItems: (
                state,
                action
            ) => {
                state.returnableItems =
                    Array.isArray(
                        action.payload
                    )
                        ? action.payload
                        : [];
            },

            updateReturnableItem: (
                state,
                action
            ) => {
                const {
                    invoiceItemId,
                    changes,
                } =
                    action.payload || {};

                if (!invoiceItemId) {
                    return;
                }

                const index =
                    state.returnableItems.findIndex(
                        (item) =>
                            String(
                                item.invoiceItemId ||
                                item._id
                            ) ===
                            String(
                                invoiceItemId
                            )
                    );

                if (index >= 0) {
                    state.returnableItems[index] = {
                        ...state.returnableItems[
                        index
                        ],
                        ...(changes || {}),
                    };
                }
            },

            clearSaleReturnHistory: (
                state
            ) => {
                state.history = [];

                state.historyPagination = {
                    ...initialPagination,
                };

                state.historyLoading =
                    false;
            },

            clearSaleReturnErrors: (
                state
            ) => {
                state.error = null;
                state.actionError = null;
            },

            clearSaleReturnOperation: (
                state
            ) => {
                state.operationSuccess =
                    false;

                state.operationMessage =
                    "";

                state.actionError =
                    null;
            },

            clearLastSaleReturnResult: (
                state
            ) => {
                state.lastCreatedSaleReturn =
                    null;

                state.lastConfirmedSaleReturn =
                    null;

                state.lastCreditNote =
                    null;

                state.lastRefund =
                    null;

                state.lastStockMovements =
                    [];
            },

            resetSaleReturnState:
                () => ({
                    ...initialState,

                    filters: {
                        ...initialFilters,
                    },

                    pagination: {
                        ...initialPagination,
                    },

                    historyPagination: {
                        ...initialPagination,
                    },

                    summary: {
                        ...initialSummary,
                    },

                    returnableSummary: {
                        ...initialReturnableSummary,
                    },
                }),
        },

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturns.pending,
                    (state) => {
                        state.listLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturns.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.listLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.saleReturns =
                            payload.saleReturns ||
                            payload.records ||
                            payload.items ||
                            [];

                        state.pagination = {
                            ...initialPagination,
                            ...(payload.pagination ||
                                {}),
                        };

                        if (
                            payload.pagination
                                ?.page
                        ) {
                            state.filters.page =
                                payload.pagination.page;
                        }

                        if (
                            payload.pagination
                                ?.limit
                        ) {
                            state.filters.limit =
                                payload.pagination.limit;
                        }
                    }
                )
                .addCase(
                    fetchSaleReturns.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.listLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale returns.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturnSummary.pending,
                    (state) => {
                        state.summaryLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturnSummary.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.summaryLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.summary = {
                            ...initialSummary,
                            ...(payload.summary ||
                                payload),
                        };
                    }
                )
                .addCase(
                    fetchSaleReturnSummary.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.summaryLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale return summary.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREATE DATA
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturnCreateData.pending,
                    (state) => {
                        state.createDataLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturnCreateData.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.createDataLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.createData =
                            payload;

                        if (payload.invoice) {
                            state.returnableInvoice =
                                payload.invoice;
                        }

                        if (
                            Array.isArray(
                                payload.returnableItems
                            )
                        ) {
                            state.returnableItems =
                                payload.returnableItems;
                        }

                        if (
                            payload.returnableSummary
                        ) {
                            state.returnableSummary = {
                                ...initialReturnableSummary,
                                ...payload.returnableSummary,
                            };
                        }

                        if (payload.dropdowns) {
                            state.dropdowns = {
                                ...state.dropdowns,
                                ...payload.dropdowns,
                            };
                        }
                    }
                )
                .addCase(
                    fetchSaleReturnCreateData.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.createDataLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale return create data.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | DROPDOWNS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturnDropdowns.pending,
                    (state) => {
                        state.dropdownsLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturnDropdowns.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.dropdownsLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.dropdowns = {
                            ...state.dropdowns,
                            ...(payload.dropdowns ||
                                payload),
                        };
                    }
                )
                .addCase(
                    fetchSaleReturnDropdowns.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.dropdownsLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale return dropdowns.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | RETURNABLE INVOICE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchReturnableInvoice.pending,
                    (state) => {
                        state.returnableInvoiceLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchReturnableInvoice.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.returnableInvoiceLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.returnableInvoice =
                            payload.invoice ||
                            payload;
                    }
                )
                .addCase(
                    fetchReturnableInvoice.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.returnableInvoiceLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch returnable invoice.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | RETURNABLE ITEMS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchReturnableInvoiceItems.pending,
                    (state) => {
                        state.returnableItemsLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchReturnableInvoiceItems.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.returnableItemsLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.returnableInvoice =
                            payload.invoice ||
                            state.returnableInvoice;

                        state.returnableItems =
                            payload.items ||
                            [];

                        state.returnableSummary = {
                            ...initialReturnableSummary,
                            ...(payload.summary ||
                                {}),
                        };
                    }
                )
                .addCase(
                    fetchReturnableInvoiceItems.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.returnableItemsLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch returnable invoice items.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturnById.pending,
                    (state) => {
                        state.detailsLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturnById.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.detailsLoading =
                            false;

                        state.selectedSaleReturn =
                            extractSaleReturn(
                                action.payload
                            );
                    }
                )
                .addCase(
                    fetchSaleReturnById.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.detailsLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale return.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createSaleReturn.pending,
                    (state) => {
                        state.createLoading =
                            true;

                        state.actionError =
                            null;

                        state.operationSuccess =
                            false;

                        state.operationMessage =
                            "";
                    }
                )
                .addCase(
                    createSaleReturn.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.createLoading =
                            false;

                        const payload =
                            action.payload?.data ||
                            {};

                        const saleReturn =
                            extractSaleReturn(
                                payload
                            );

                        state.lastCreatedSaleReturn =
                            saleReturn;

                        state.selectedSaleReturn =
                            saleReturn;

                        state.lastCreditNote =
                            payload.creditNote ||
                            null;

                        state.lastRefund =
                            payload.refund ||
                            null;

                        state.lastStockMovements =
                            payload.stockMovements ||
                            [];

                        addSaleReturnToList(
                            state,
                            saleReturn
                        );

                        state.operationSuccess =
                            true;

                        state.operationMessage =
                            action.payload?.message ||
                            "Sale return created successfully.";
                    }
                )
                .addCase(
                    createSaleReturn.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.createLoading =
                            false;

                        state.actionError =
                            action.payload ||
                            action.error?.message ||
                            "Unable to create sale return.";

                        state.operationSuccess =
                            false;
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CONFIRM
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    confirmSaleReturn.pending,
                    (state) => {
                        state.confirmLoading =
                            true;

                        state.actionError =
                            null;

                        state.operationSuccess =
                            false;
                    }
                )
                .addCase(
                    confirmSaleReturn.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.confirmLoading =
                            false;

                        const payload =
                            action.payload?.data ||
                            {};

                        const saleReturn =
                            extractSaleReturn(
                                payload
                            );

                        state.lastConfirmedSaleReturn =
                            saleReturn;

                        state.selectedSaleReturn =
                            saleReturn;

                        state.lastCreditNote =
                            payload.creditNote ||
                            null;

                        state.lastRefund =
                            payload.refund ||
                            null;

                        state.lastStockMovements =
                            payload.stockMovements ||
                            [];

                        updateSaleReturnInList(
                            state,
                            saleReturn
                        );

                        state.operationSuccess =
                            true;

                        state.operationMessage =
                            action.payload?.message ||
                            "Sale return confirmed successfully.";
                    }
                )
                .addCase(
                    confirmSaleReturn.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.confirmLoading =
                            false;

                        state.actionError =
                            action.payload ||
                            action.error?.message ||
                            "Unable to confirm sale return.";

                        state.operationSuccess =
                            false;
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CANCEL
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    cancelSaleReturn.pending,
                    (state) => {
                        state.cancelLoading =
                            true;

                        state.actionError =
                            null;

                        state.operationSuccess =
                            false;
                    }
                )
                .addCase(
                    cancelSaleReturn.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.cancelLoading =
                            false;

                        const saleReturn =
                            extractSaleReturn(
                                action.payload?.data
                            );

                        state.selectedSaleReturn =
                            saleReturn;

                        updateSaleReturnInList(
                            state,
                            saleReturn
                        );

                        state.operationSuccess =
                            true;

                        state.operationMessage =
                            action.payload?.message ||
                            "Sale return cancelled successfully.";
                    }
                )
                .addCase(
                    cancelSaleReturn.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.cancelLoading =
                            false;

                        state.actionError =
                            action.payload ||
                            action.error?.message ||
                            "Unable to cancel sale return.";

                        state.operationSuccess =
                            false;
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | DELETE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    deleteSaleReturn.pending,
                    (state) => {
                        state.deleteLoading =
                            true;

                        state.actionError =
                            null;

                        state.operationSuccess =
                            false;
                    }
                )
                .addCase(
                    deleteSaleReturn.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.deleteLoading =
                            false;

                        const saleReturnId =
                            action.payload
                                ?.saleReturnId;

                        state.saleReturns =
                            state.saleReturns.filter(
                                (item) =>
                                    item._id !==
                                    saleReturnId
                            );

                        if (
                            state
                                .selectedSaleReturn
                                ?._id ===
                            saleReturnId
                        ) {
                            state.selectedSaleReturn =
                                null;
                        }

                        state.operationSuccess =
                            true;

                        state.operationMessage =
                            action.payload?.message ||
                            "Sale return deleted successfully.";
                    }
                )
                .addCase(
                    deleteSaleReturn.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.deleteLoading =
                            false;

                        state.actionError =
                            action.payload ||
                            action.error?.message ||
                            "Unable to delete sale return.";

                        state.operationSuccess =
                            false;
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | HISTORY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSaleReturnHistory.pending,
                    (state) => {
                        state.historyLoading =
                            true;

                        state.error = null;
                    }
                )
                .addCase(
                    fetchSaleReturnHistory.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.historyLoading =
                            false;

                        const payload =
                            action.payload || {};

                        state.history =
                            payload.history ||
                            payload.records ||
                            payload.items ||
                            [];

                        state.historyPagination = {
                            ...initialPagination,
                            ...(payload.pagination ||
                                {}),
                        };
                    }
                )
                .addCase(
                    fetchSaleReturnHistory.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.historyLoading =
                            false;

                        state.error =
                            action.payload ||
                            action.error?.message ||
                            "Unable to fetch sale return history.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setSaleReturnFilters,
    setSaleReturnFilter,
    setSaleReturnPage,
    setSaleReturnLimit,
    resetSaleReturnFilters,

    setSelectedSaleReturn,
    clearSelectedSaleReturn,

    clearReturnableInvoice,
    setReturnableItems,
    updateReturnableItem,

    clearSaleReturnHistory,
    clearSaleReturnErrors,
    clearSaleReturnOperation,
    clearLastSaleReturnResult,

    resetSaleReturnState,
} = saleReturnSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const saleReturnSelector = (
    state
) => state.saleReturn;

export const saleReturnListSelector = (
    state
) =>
    state.saleReturn.saleReturns;

export const selectedSaleReturnSelector = (
    state
) =>
    state.saleReturn
        .selectedSaleReturn;

export const saleReturnSummarySelector = (
    state
) =>
    state.saleReturn.summary;

export const saleReturnFiltersSelector = (
    state
) =>
    state.saleReturn.filters;

export const saleReturnPaginationSelector = (
    state
) =>
    state.saleReturn.pagination;

export const saleReturnCreateDataSelector = (
    state
) =>
    state.saleReturn.createData;

export const saleReturnDropdownsSelector = (
    state
) =>
    state.saleReturn.dropdowns;

export const returnableInvoiceSelector = (
    state
) => ({
    invoice:
        state.saleReturn
            .returnableInvoice,

    items:
        state.saleReturn
            .returnableItems,

    summary:
        state.saleReturn
            .returnableSummary,

    invoiceLoading:
        state.saleReturn
            .returnableInvoiceLoading,

    itemsLoading:
        state.saleReturn
            .returnableItemsLoading,
});

export const saleReturnHistorySelector = (
    state
) => ({
    history:
        state.saleReturn.history,

    pagination:
        state.saleReturn
            .historyPagination,

    loading:
        state.saleReturn
            .historyLoading,
});

export const saleReturnLoadingSelector = (
    state
) => ({
    listLoading:
        state.saleReturn
            .listLoading,

    detailsLoading:
        state.saleReturn
            .detailsLoading,

    summaryLoading:
        state.saleReturn
            .summaryLoading,

    createDataLoading:
        state.saleReturn
            .createDataLoading,

    dropdownsLoading:
        state.saleReturn
            .dropdownsLoading,

    historyLoading:
        state.saleReturn
            .historyLoading,

    returnableInvoiceLoading:
        state.saleReturn
            .returnableInvoiceLoading,

    returnableItemsLoading:
        state.saleReturn
            .returnableItemsLoading,

    createLoading:
        state.saleReturn
            .createLoading,

    confirmLoading:
        state.saleReturn
            .confirmLoading,

    cancelLoading:
        state.saleReturn
            .cancelLoading,

    deleteLoading:
        state.saleReturn
            .deleteLoading,
});

export const saleReturnOperationSelector = (
    state
) => ({
    success:
        state.saleReturn
            .operationSuccess,

    message:
        state.saleReturn
            .operationMessage,

    error:
        state.saleReturn
            .actionError,

    lastCreatedSaleReturn:
        state.saleReturn
            .lastCreatedSaleReturn,

    lastConfirmedSaleReturn:
        state.saleReturn
            .lastConfirmedSaleReturn,

    lastCreditNote:
        state.saleReturn
            .lastCreditNote,

    lastRefund:
        state.saleReturn
            .lastRefund,

    lastStockMovements:
        state.saleReturn
            .lastStockMovements,
});

export default saleReturnSlice.reducer;